/**
 * Celebration Store (Pinia)
 *
 * Manages global celebration overlay state.
 * When a celebration is triggered, it sets visibility + text,
 * then auto-hides after 2 seconds.
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';

export type CelebrationType =
  | 'prediction_correct'
  | 'announcer_beaten'
  | 'draw_entered'
  | 'auction_won'
  | 'achievement_unlocked'
  | 'level_up';

export interface CelebrationOptions {
  type: CelebrationType;
  points?: number;
  text?: string;
  subText?: string;
}

const TYPE_DEFAULTS: Record<CelebrationType, { mainText: string; subTextTemplate: string }> = {
  prediction_correct: { mainText: 'CORRECT!', subTextTemplate: '+{pts} PTS' },
  announcer_beaten: { mainText: 'YOU BEAT THE ANNOUNCER!', subTextTemplate: '+{pts} BONUS!' },
  draw_entered: { mainText: 'TICKET LOCKED IN!', subTextTemplate: '#{ticketNumber}' },
  auction_won: { mainText: 'LOT WON!', subTextTemplate: '\u00A3{amount}' },
  achievement_unlocked: { mainText: 'ACHIEVEMENT!', subTextTemplate: '{achievementName}' },
  level_up: { mainText: 'LEVEL UP!', subTextTemplate: '{levelName}' },
};

export const useCelebrationStore = defineStore('celebration', () => {
  const isVisible = ref(false);
  const type = ref<CelebrationType | null>(null);
  const mainText = ref('');
  const subText = ref('');

  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  function show(options: CelebrationOptions) {
    // Clear any existing timer
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    const defaults = TYPE_DEFAULTS[options.type];

    type.value = options.type;
    mainText.value = options.text ?? defaults.mainText;
    subText.value =
      options.subText ??
      (options.points != null
        ? defaults.subTextTemplate.replace('{pts}', String(options.points))
        : defaults.subTextTemplate);
    isVisible.value = true;

    hideTimer = setTimeout(() => {
      isVisible.value = false;
      type.value = null;
      mainText.value = '';
      subText.value = '';
      hideTimer = null;
    }, 2000);
  }

  return { isVisible, type, mainText, subText, show };
});
